// Client-side, AI-SAFE image compression + instant local previews.
//
// Phase 1 media optimization. A modern phone photo (5-12MB) is downscaled to a
// 2048px bounding box and re-encoded as JPEG @0.85 BEFORE upload, cutting the
// payload to ~300-500KB so device uploads drop from ~10s to <1s — while
// preserving enough pixel clarity for our image-to-image / face pipelines.
//
// Guardrails:
// - Only raster photos are touched (jpeg/png/webp). GIF/SVG/video/audio and any
//   non-image are returned UNCHANGED (animation & vectors preserved).
// - 2048px cap, quality 0.85 (never below 0.80).
// - Never upsizes and never blocks an upload: any failure returns the original.

const RASTER = /^image\/(jpe?g|png|webp)$/i;

function decodeViaImg(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

export async function compressImage(file, { maxDim = 2048, quality = 0.85, mime = "image/jpeg" } = {}) {
  try {
    if (!file || !file.type || !RASTER.test(file.type)) return file;   // skip non-photos
    const q = Math.max(0.8, quality);

    let src;
    try { src = await createImageBitmap(file); }
    catch { src = await decodeViaImg(file); }

    const iw = src.width, ih = src.height;
    if (!iw || !ih) { if (src.close) src.close(); return file; }

    const scale = Math.min(1, maxDim / Math.max(iw, ih));
    const w = Math.round(iw * scale), h = Math.round(ih * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(src, 0, 0, w, h);
    if (src.close) src.close();

    const blob = await new Promise((res) => canvas.toBlob(res, mime, q));
    if (!blob) return file;
    // No resize happened AND we didn't shrink the bytes → keep the original.
    if (scale === 1 && blob.size >= file.size) return file;

    const base = (file.name || "image").replace(/\.[^.]+$/, "");
    return new File([blob], `${base}.jpg`, { type: mime, lastModified: Date.now() });
  } catch {
    return file;   // compression must never block an upload
  }
}

// Instant local preview URL for optimistic UI. Caller should revoke it when done
// (or on unmount) to avoid leaking object URLs.
export function previewUrl(file) {
  try { return file ? URL.createObjectURL(file) : ""; } catch { return ""; }
}

// Reference-picker compression engine. Downscales to an aspect-aware bounding box
// (9:16 → 576×1024, 16:9 → 1024×576; longest edge ≤1024, AR preserved, no crop)
// then runs a DYNAMIC quality loop: start 0.9, step down by 0.10 until the JPEG
// sits at/under `targetKB` (default 300KB) or hits the quality floor. Always emits
// a clean image/jpeg File so it renders/downloads correctly in native galleries.
export async function compressToTarget(file, {
  maxW = 1024, maxH = 1024, targetKB = 300, minQuality = 0.3, startQuality = 0.9,
} = {}) {
  try {
    if (!file || !file.type || !/^image\//i.test(file.type)) return file;
    let src;
    try { src = await createImageBitmap(file); }
    catch { src = await decodeViaImg(file); }
    const iw = src.width, ih = src.height;
    if (!iw || !ih) { if (src.close) src.close(); return file; }

    const scale = Math.min(1, maxW / iw, maxH / ih);           // AR preserved, no crop
    const w = Math.max(1, Math.round(iw * scale));
    const h = Math.max(1, Math.round(ih * scale));

    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.fillStyle = "#ffffff";                                  // flatten any alpha for JPEG
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(src, 0, 0, w, h);
    if (src.close) src.close();

    const targetBytes = targetKB * 1024;
    const toBlob = (q) => new Promise((res) => canvas.toBlob(res, "image/jpeg", q));
    let q = startQuality;
    let blob = await toBlob(q);
    while (blob && blob.size > targetBytes && q > minQuality) {
      q = Math.max(minQuality, Math.round((q - 0.10) * 100) / 100);
      blob = await toBlob(q);
    }
    if (!blob) return file;
    const base = (file.name || "image").replace(/\.[^.]+$/, "");
    return new File([blob], `${base}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file;   // compression must never block the picker
  }
}
